import { useCallback, useRef } from 'react'
import { db } from '../../../lib/db/indexeddb'
import { scheduleSync } from '../../../lib/sync/sync-engine'
import type { Task } from '../../../lib/db/models'

interface UseContextMenuBadgeUpdateParams {
  /** Read the currently-selected task ids at the moment the context menu
   *  fires. Accepts a function so callers with internal selection state
   *  (TaskList handle) and callers with external state (ProjectPage
   *  navigation) can both plug in without extra wiring. */
  getSelection: () => Set<string>
  /** Optimistic-first sink — the badge update should be visible in the
   *  next paint, not after the IDB → LiveQuery roundtrip. */
  setOptimisticTaskOverride: (taskId: string, partial: Partial<Task>) => void
  /** Page identifier that gets prefixed to the error log so failures are
   *  attributable (e.g. `'DormantPage'`). */
  logTag: string
}

/**
 * Right-click badge update shared between QuickNotesPage, ProjectPage,
 * and DormantPage. Semantics:
 *  - If the clicked task is part of a multi-selection, apply to all
 *    selected ids; otherwise apply to just the clicked task.
 *  - Writes + sync-queue entries run inside a single Dexie transaction
 *    so a mid-way failure doesn't leave a partial update across rows.
 */
export function useContextMenuBadgeUpdate({
  getSelection,
  setOptimisticTaskOverride,
  logTag,
}: UseContextMenuBadgeUpdateParams) {
  // Hold the caller's getSelection in a ref so the returned callback ref
  // stays stable across renders even when the caller passes an inline
  // arrow (common pattern). Without this, every parent render would
  // create a new callback ref and bust downstream memoization.
  const getSelectionRef = useRef(getSelection)
  getSelectionRef.current = getSelection

  return useCallback(async (
    clickedTaskId: string,
    updates: Partial<Omit<Task, 'id'>>,
  ) => {
    const selection = getSelectionRef.current()
    const targetIds = selection.size > 1 && selection.has(clickedTaskId)
      ? Array.from(selection)
      : [clickedTaskId]
    for (const id of targetIds) setOptimisticTaskOverride(id, updates)
    try {
      const now = new Date()
      await db.transaction('rw', db.tasks, db.syncQueue, async () => {
        for (const id of targetIds) {
          await db.tasks.update(id, { ...updates, updatedAt: now, lastModified: now, synced: false })
          await db.queueSyncChange('update', 'tasks', { localId: id })
        }
      })
      scheduleSync()
    } catch (err) {
      console.error(`[${logTag}] Failed to apply badge update:`, err)
    }
  }, [setOptimisticTaskOverride, logTag])
}
